import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { AppMaterialModule } from './app.material.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { SessionStorageService } from '../services/jng2-webstorage/app';

import { AdminSettingsComponent } from './admin-settings/admin-settings.component';
import { AdoptionCourseSearchDialogComponent } from '../services/dialogs/adoptioncoursesearch/adoptioncoursesearch.dialogcomponent';
import { AdoptionRequestComponent } from './adoptionrequest/adoptionrequest.component';
import { AgreementsPanelComponent } from './ui/agreements/agreements.component';
import { AppRoutingModule } from '../routes/app-routing.module';
import { AppComponent } from './app.component';
import { AuthenticatedGuard } from '../routes/routing-guard';
import { CheckboxDialogComponent } from '../services/dialogs/checkbox/checkbox.dialogcomponent';
import { ClassicNotificationDialogComponent } from '../services/dialogs/classicnotification/classicnotification.dialogcomponent';
import { ClasslinkSSOLoginComponent } from './classlink/classlink.component';
import { CollegeStudentSearchComponent } from './collegestudentsearch/collegestudentsearch.component';
import { ConfirmationDialogComponent } from '../services/dialogs/confirmation/confirmation.dialogcomponent';
import { CountdownDialogComponent } from '../services/dialogs/countdown/countdown.dialogcomponent';
import { CourseChoiceDialogComponent } from '../services/dialogs/coursechoice/coursechoice.dialogcomponent';
import { CourseDescriptionDialogComponent } from '../services/dialogs/coursedescription/coursedescription.dialogcomponent';
import { CourseNoteDialogComponent } from '../services/dialogs/coursenote/coursenote.dialogcomponent';
import { CourseSelectionPanelComponent } from './ui/courseselectionpanel/courseselectionpanel.component';
import { FormLabelComponent } from './ui/formlabel/formlabel.component';
import { FullScheduleDialogComponent } from '../services/dialogs/fullschedule/fullschedule.dialogcomponent';
import { HomeComponent } from './home/home.component';
import { InputBoxDialogComponent } from '../services/dialogs/inputbox/inputbox.dialogcomponent';
import { IssueAlertComponent } from './issuealert/issuealert.component';
import { IssueReportComponent } from './issuereport/issuereport.component';
import { IssueReportReviewComponent } from './issuereportreview/issuereportreview.component';
import { ManageAdoptionsComponent } from './manageadoptions/manageadoptions.component';
import { ManageScoresComponent } from './managescores/managescores.component';
import { ManageStaffComponent } from './managestaff/managestaff.component';
import { ManageCoursesComponent } from './managecourses/managecourses.component';
import { ManageDesigneesComponent } from './managedesignees/managedesignees.component';
import { ManageTermsComponent } from './manageterms/manageterms.component';
import { MessageBoxDialogComponent } from '../services/dialogs/messagebox/messagebox.dialogcomponent';
import { NavMenuComponent } from './ui/navmenu/navmenu.component';
import { NotificationDialogComponent } from '../services/dialogs/notification/notification.dialogcomponent';
import { OfflineComponent } from './offline/offline.component';
import { ParentContactComponent } from './parentcontact/parentcontact.component';
import { ProxyApproveDialogComponent } from '../services/dialogs/proxyapprove/proxyapprove.dialogcomponent';
import { OldRequestsComponent } from './oldrequests/oldrequests.component';
import { OptionBoxDialogComponent } from '../services/dialogs/optionbox/optionbox.dialogcomponent';
import { PageNotFoundComponent } from './pagenotfound/pagenotfound.component';
import { ReportOptionsDialogComponent } from '../services/dialogs/reportoptions/reportoptions.dialogcomponent';
import { ReportsComponent } from './reports/reports.component';
import { RequestNotesComponent } from './ui/requestnotes/requestnotes.component';
import { RequestsComponent } from './requests/requests.component';
import { ScheduleCourseDialogComponent } from '../services/dialogs/schedulecourse/schedulecourse.dialogcomponent';
import { StudentSelectComponent } from './studentselect/studentselect.component';
import { SplashDialogComponent } from '../services/splash/splash.component';
import { SSOLoginComponent } from './ssologin/ssologin.component';
import { StudentCollegeRecordsComponent } from './ui/studentcollegerecords/studentcollegerecords.component';
import { StudentContactComponent } from './studentcontact/studentcontact.component';
import { StudentInfoComponent } from './ui/studentinfo/studentinfo.component';
import { StudentSearchComponent } from './studentsearch/studentsearch.component';
import { StudentSearchDialogComponent } from '../services/dialogs/studentsearch/studentsearch.dialogcomponent';
import { TagsComponent } from './ui/tags/tags.component';
import { TestHistoryComponent } from './ui/testhistory/testhistory.component';
import { TextNoteDialogComponent } from '../services/dialogs/textnote/textnote.dialogcomponent';
import { TimelineComponent } from './ui/timeline/timeline.component';
import { UserSearchDialogComponent } from '../services/dialogs/usersearch/usersearch.dialogcomponent';
import { UpdateCollegeIDDialogComponent } from '../services/dialogs/updatecollegeID/updatecollegeID.dialogcomponent';
import { UserInfoComponent } from './userinfo/userinfo.component';
import { LoginComponent } from './login/login.component';

import { ApplicationService } from '../services/application/application.service';
import { AuthService } from '../services/authentication/auth.service';
import { ConfigService } from '../services/config/config.service';
import { DialogService } from '../services/dialogs/dialogs.service';
import { DownloadService } from '../services/download/download.service';
import { ErrorService } from '../services/error/error.service';
import { NotificationService } from '../services/notification/notification.service';
import { ReportsService } from '../services/reports/reports.service';
import { SplashService } from '../services/splash/splash.service';
import { UserService } from '../services/user/user.service';

import { AcceleratedDegreeProgramPipe } from '../pipes/accelerateddegreeprogram.pipe';
import { AgePipe } from '../pipes/age.pipe';
import { CollegeAbbreviationPipe } from '../pipes/collegeabbreviation.pipe';
import { CollegeSchoolYearPipe } from '../pipes/collegeschoolyear.pipe';
import { CollegeTermPipe } from '../pipes/collegeterm.pipe';
import { CounselorNamePipe } from '../pipes/counselorname.pipe';
import { CourseRequestStatusPipe } from '../pipes/courserequeststatus.pipe';
import { DateFnsPipe } from '../pipes/datefns.pipe';
import { DualEnrollmentCreditsPipe } from '../pipes/dualenrollmentcredits.pipe';
import { DualEnrollmentProgramPipe } from '../pipes/dualenrollmentprogram.pipe';
import { EligibleCoursesPipe } from '../pipes/eligible-courses.pipe';
import { ExtendedCollegeTermPipe } from '../pipes/extendedcollegeterm.pipe';
import { FormStatusPipe } from '../pipes/formstatus.pipe';
import { PhonePipe } from '../pipes/phone.pipe';
import { RoleTextPipe } from '../pipes/roletext.pipe';
import { SafeHTMLPipe } from '../pipes/safehtml.pipe';
import { SchoolYearPipe } from '../pipes/schoolyear.pipe';
import { SocialSecurityPipe } from '../pipes/socialsecurity.pipe';
import { TitleCasePipe } from '../pipes/titlecase.pipe';
import { YesNoPipe } from '../pipes/yesno.pipe';
import { ZipcodePipe } from '../pipes/zipcode.pipe';

import { MobileTapDirective } from '../classes/MobileTap/mobiletap.directive';
import { EligibleCoursesDialogComponent } from '../services/dialogs/eligiblecourses/eligiblecourses.dialogcomponent';
import { EditTestThresholdDialogComponent } from '../services/dialogs/edittestthreshold/edittestthreshold.dialogcomponent';
import { CollegeNamePipe } from '../pipes/collegename.pipe';
import { CollegeContactComponent } from './admin-settings/components/college-contact/college-contact.component';
import { EmailTestComponent } from './admin-settings/components/email-test/email-test.component';
// import { CollegeStudentSearchDialogComponent } from '../services/dialogs/collegestudentsearch/collegestudentsearch.dialogcomponent';

@NgModule({
    declarations: [
        AdminSettingsComponent,
        AdoptionCourseSearchDialogComponent,
        AdoptionRequestComponent,
        AgreementsPanelComponent,
        AppComponent,
        CheckboxDialogComponent,
        ClassicNotificationDialogComponent,
        ClasslinkSSOLoginComponent,
        CollegeContactComponent,
        // CollegeStudentSearchDialogComponent,
        CollegeStudentSearchComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        CourseChoiceDialogComponent,
        CourseDescriptionDialogComponent,
        CourseNoteDialogComponent,
        CourseSelectionPanelComponent,
        EditTestThresholdDialogComponent,
        EligibleCoursesDialogComponent,
        EmailTestComponent,
        FormLabelComponent,
        FullScheduleDialogComponent,
        HomeComponent,
        InputBoxDialogComponent,
        IssueAlertComponent,
        IssueReportComponent,
        IssueReportReviewComponent,
        LoginComponent,
        ManageAdoptionsComponent,
        ManageCoursesComponent,
        ManageDesigneesComponent,
        ManageScoresComponent,
        ManageStaffComponent,
        ManageTermsComponent,
        MessageBoxDialogComponent,
        NavMenuComponent,
        NotificationDialogComponent,
        OfflineComponent,
        OldRequestsComponent,
        OptionBoxDialogComponent,
        PageNotFoundComponent,
        ParentContactComponent,
        ProxyApproveDialogComponent,
        ReportOptionsDialogComponent,
        ReportsComponent,
        RequestNotesComponent,
        RequestsComponent,
        ScheduleCourseDialogComponent,
        SplashDialogComponent,
        SSOLoginComponent,
        StudentCollegeRecordsComponent,
        StudentContactComponent,
        StudentInfoComponent,
        StudentSearchComponent,
        StudentSearchDialogComponent,
        StudentSelectComponent,
        TagsComponent,
        TestHistoryComponent,
        TextNoteDialogComponent,
        TimelineComponent,
        UserInfoComponent,
        UpdateCollegeIDDialogComponent,
        UserSearchDialogComponent,

        // Pipes
        AcceleratedDegreeProgramPipe,
        AgePipe,
        CollegeAbbreviationPipe,
        CollegeSchoolYearPipe,
        CollegeNamePipe,
        CollegeTermPipe,
        CounselorNamePipe,
        CourseRequestStatusPipe,
        DateFnsPipe,
        DualEnrollmentCreditsPipe,
        DualEnrollmentProgramPipe,
        EligibleCoursesPipe,
        ExtendedCollegeTermPipe,
        FormStatusPipe,
        PhonePipe,
        RoleTextPipe,
        SafeHTMLPipe,
        SchoolYearPipe,
        SocialSecurityPipe,
        TitleCasePipe,
        YesNoPipe,
        ZipcodePipe,

        // Directives
        MobileTapDirective,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppMaterialModule,
        FlexLayoutModule,
        AppRoutingModule,
    ],
    providers: [
        AcceleratedDegreeProgramPipe,
        ApplicationService,
        AuthenticatedGuard,
        AuthService,
        CollegeSchoolYearPipe,
        CollegeTermPipe,
        CollegeNamePipe,
        ConfigService,
        CourseRequestStatusPipe,
        DateFnsPipe,
        DatePipe,
        DecimalPipe,
        DialogService,
        DownloadService,
        DualEnrollmentProgramPipe,
        EligibleCoursesPipe,
        ErrorService,
        ExtendedCollegeTermPipe,
        FormStatusPipe,
        NotificationService,
        PhonePipe,
        ReportsService,
        RoleTextPipe,
        SchoolYearPipe,
        SessionStorageService,
        SplashService,
        TitleCasePipe,
        UserService,
        YesNoPipe,
    ],
    entryComponents: [
        AdoptionCourseSearchDialogComponent,
        CheckboxDialogComponent,
        ClassicNotificationDialogComponent,
        // CollegeStudentSearchDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        CourseChoiceDialogComponent,
        CourseDescriptionDialogComponent,
        CourseNoteDialogComponent,
        EditTestThresholdDialogComponent,
        EligibleCoursesDialogComponent,
        FullScheduleDialogComponent,
        InputBoxDialogComponent,
        MessageBoxDialogComponent,
        NotificationDialogComponent,
        OptionBoxDialogComponent,
        ProxyApproveDialogComponent,
        ReportOptionsDialogComponent,
        ScheduleCourseDialogComponent,
        SplashDialogComponent,
        StudentSearchDialogComponent,
        TextNoteDialogComponent,
        UpdateCollegeIDDialogComponent,
        UserSearchDialogComponent,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts